<script setup>
import BaseButton from '@/components/Base/BaseButton.vue';

defineProps({
  hideFooter: {
    type: Boolean,
    required: false,
    default() {
      return false;
    },
  },
  headingClass: {
    type: String,
    required: false,
    default() {
      return '';
    },
  },
  cancelButtonText: {
    type: String,
    required: false,
    default() {
      return 'Cancel';
    },
  },
  cancelButtonState: {
    type: String,
    required: false,
    default() {
      return 'danger';
    },
  },
  confirmButtonState: {
    type: String,
    required: false,
    default() {
      return 'primary';
    },
  },
  confirmButtonText: {
    type: String,
    required: false,
    default() {
      return 'Confirm';
    },
  },
  confirmButtonType: {
    type: String,
    required: false,
    default() {
      return 'button';
    },
  },
  confirmButtonDestructive: {
    type: Boolean,
    required: false,
    default() {
      return false;
    },
  },
  disabled: {
    type: Boolean,
    required: false,
    default() {
      return false;
    },
  },
  isLoading: {
    type: Boolean,
    required: false,
    default() {
      return false;
    },
  },
  onClose: {
    type: Function,
    required: false,
    default() {
      return () => {};
    },
  },
  onConfirm: {
    type: Function,
    required: false,
    default() {
      return () => {};
    },
  },
  width: {
    type: String,
    required: false,
    default() {
      return '400px';
    },
  },
  paddingless: {
    type: Boolean,
    required: false,
    default() {
      return false;
    },
  },
  footerBorder: {
    type: Boolean,
    required: false,
    default() {
      return false;
    },
  },
});
</script>

<template>
  <div
    class="!rounded-[12px] bg-white shadow-xl relative card-wrapper"
    :style="{ width }"
  >
    <BaseButton
      v-if="onClose"
      icon-prepend="x-close"
      class="p-2.5 absolute top-4 right-4 text-proxify-gray-500"
      @click="onClose"
    />
    <div
      class="shrink-0 p-6 flex flex-col gap-5"
      :class="headingClass"
    >
      <div
        class="text-body-lg font-semibold text-proxify-gray-900 whitespace-normal flex justify-between"
      >
        <slot name="title" />
      </div>
      <slot name="subtitle" />
    </div>
    <div
      class="text-proxify-gray-600 text-body-sm font-medium overflow-y-auto proxify-scrollbar content"
      :class="{
        'border-b border-proxify-gray-200': footerBorder,
        'px-6': !paddingless,
        'px-0': paddingless,
      }"
    >
      <slot />
    </div>
    <div
      v-if="!hideFooter"
      class="footer"
    >
      <slot name="footer">
        <div
          class="flex flex-row justify-between gap-3 p-6"
          :class="{ 'px-6': paddingless }"
        >
          <BaseButton
            v-if="cancelButtonText"
            rounded
            class="icon-secondary-gray w-full whitespace-nowrap text-body-md font-semibold"
            @click="onClose"
          >
            {{ cancelButtonText }}
          </BaseButton>
          <BaseButton
            rounded
            class="icon-primary w-full whitespace-nowrap text-body-md font-semibold"
            :class="{ 'button-destructive': confirmButtonDestructive }"
            :disabled="disabled"
            :loading="isLoading"
            :type="confirmButtonType"
            data-testid="confirm-button"
            @click="onConfirm"
          >
            {{ confirmButtonText }}
          </BaseButton>
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.card-wrapper {
  display: flex;
  flex-direction: column;
  height: auto;
}

.content {
  overflow-y: auto;
  flex-grow: 1;
  max-height: 70vh;
  padding-bottom: 20px;
}

.footer {
  z-index: 1;
  flex-shrink: 0;
  box-shadow: 0 -10px 10px -10px #ebebeb;
}
</style>
